// src/api/authService.ts
import axios from 'axios';
import axiosInstance from './axiosInstance';

const BASE_URL = process.env.REACT_APP_API_URL;

export const login = async (email: string, password: string) => {
  try {
    const response = await axios.post(`${BASE_URL}/auth/login`, { email, password });
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.data) {
      throw err.response.data;
    }
    throw err;
  }
};

export const refreshToken = async (token: string) => {
  try {
    const response = await axios.post(`${BASE_URL}/auth/token`, { token });
    return response.data.accessToken;
  } catch (err) {
    console.error('Error en la solicitud de refresco de token:', err);
    throw new Error('Error en el refresco del token');
  }
};

/**
 * Valida la sesión actual con el backend
 * Retorna true si la sesión es válida, false en caso contrario
 */
export const validateSession = async (): Promise<boolean> => {
  try {
    await axiosInstance.get('/protected');
    return true;
  } catch (error: any) {
    // Si es 401 o 403 relacionado con autenticación, la sesión no es válida
    if (error.response?.status === 401) {
      return false;
    }
    // Si es 403 y el mensaje indica problema de autenticación
    if (error.response?.status === 403) {
      const errorMessage = error.response.data?.message || '';
      if (
        errorMessage.includes('token') ||
        errorMessage.includes('authentication') ||
        errorMessage.includes('unauthorized')
      ) {
        return false;
      }
    }
    // Si es error de validación de sesión
    if (
      error.response?.status === 500 &&
      error.response?.data?.error === 'SESSION_VALIDATION_ERROR'
    ) {
      return false;
    }
    // Para otros errores (red, etc.), asumimos que la sesión podría ser válida
    // pero hay un problema de conexión
    return true;
  }
};

const apiRequest = async (url: string, options: any) => {
  try {
    const response = await axios(url, options);
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.status === 403) {
      const refreshTokenValue = localStorage.getItem('refreshToken');
      if (refreshTokenValue) {
        try {
          const newAccessToken = await refreshToken(refreshTokenValue);
          localStorage.setItem('accessToken', newAccessToken);
          options.headers['Authorization'] = `Bearer ${newAccessToken}`;
          const retryResponse = await axios(url, options);
          return retryResponse.data;
        } catch (refreshError) {
          console.error('Error refrescando el token:', refreshError);
          throw new Error('Error refrescando el token');
        }
      } else {
        throw new Error('Token de refresco no disponible');
      }
    } else {
      throw error;
    }
  }
};

export default apiRequest;
